import {
	AfterViewInit,
	Component,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Subscription } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import {
	AlarmStatus,
	alarmOverrideText,
	armedAlarmText,
	disarmedAlarmText,
} from 'src/app/models/data/ajax';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { User } from 'src/app/models/data/user';
import { EventHistory } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { CalendarDateService } from 'src/app/services/calendar-date.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { TimeZoneService } from 'src/app/services/timezone.service';
import { UserProfile } from 'src/app/models/model';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../../components/data-set/data-set-search.component';
import { DatePipe } from '@angular/common';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	MatDatepicker,
	MatDatepickerInput,
	MatDatepickerToggle,
} from '@angular/material/datepicker';
import { DataSetTableComponent } from '../../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
	EventTypeFormatPipe,
	EndVisitTypeFormatPipe,
	EventDurationPipe,
	BillingStartDateFilterPipe,
	RequestTimeToLocal,
	ShortTimezonePipe,
} from '../../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-user-event-history',
	templateUrl: './user-event-history.component.html',
	styleUrls: [
		'./user-event-history.component.scss',
		'../../../common/main.scss',
	],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DatePipe,
		EventTypeFormatPipe,
		EndVisitTypeFormatPipe,
		EventDurationPipe,
		BillingStartDateFilterPipe,
		RequestTimeToLocal,
		ShortTimezonePipe,
		DataSetLoadingRowComponent,
	],
})
export class UserEventHistoryComponent
	implements AfterViewInit, OnChanges, OnDestroy, OnInit
{
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	AlarmStatus = AlarmStatus;
	displayedColumns: string[] = [];
	dataSource = new MatTableDataSource<EventHistory>();

	@Input() user?: User;
	@Input() companyId?: string;
	userId = '';
	canGenerateEventHistoryReport!: boolean;
	isDownloadReportDisabled = false;
	filters: QueryParam[] = [];
	companyTypeFlag?: boolean;

	filterLabels = {
		defaultPlaceholder: $localize`:@@userEventHistory-defaultPlaceholder:Search serial number...`,
		propertyName: $localize`:@@userEventHistory-filterLabelPropertyName:Property Name`,
		deviceOwner: $localize`:@@userEventHistory-filterLabelDeviceOwner:Device Owner`,
		serialNumber: $localize`:@@userEventHistory-filterLabelSerialNumber:Serial Number`,
		eventType: $localize`:@@userEventHistory-filterLabelEventType:Event Type`,
		clientName: $localize`:@@userEventHistory-filterLabelClientName:Client`,
		clientIDN: $localize`:@@userEventHistory-filterLabelClientIDN:Client IDN`,
		deviceLocation: $localize`:@@userEventHistory-filterLabelDeviceLocation:Device Location`,
		eventStart: $localize`:@@userEventHistory-filterLabelEventStart:Event Start`,
		eventEnd: $localize`:@@userEventHistory-filterLabelEventEnd:Event End`,
		endVisitType: $localize`:@@userEventHistory-filterLabelEndVisitType:End Visit Type`,
	};

	private filterSubscription?: Subscription;

	getUserEventHistoryLoadingKey = ApiLoadingKeys.getUserEventHistory;
	getEventHistoryReportLoadingKey = ApiLoadingKeys.getEventHistoryReport;
	finishedLoadingInitData: boolean = false;

	disarmedAlarmText = disarmedAlarmText;
	armedAlarmText = armedAlarmText;
	alarmOverrideText = alarmOverrideText;

	constructor(
		private apiService: ApiService,
		private readonly systemCapabilityService: SystemCapabilityService,
		public readonly loadingService: LoadingIndicatorService,
		readonly timeZoneService: TimeZoneService,
		private readonly calendarDateService: CalendarDateService
	) {}

	ngOnInit(): void {
		this.updateEventHistoryAccess();
		this.fetchCompanyType();
	}

	ngAfterViewInit(): void {
		this.userId = this.user?.id ?? '';
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				map((filters) =>
					this.calendarDateService.mapDateFiltersToCalendarDate(filters)
				),
				tap((filters) => (this.filters = filters)),
				mergeMap(async (filter) => {
					if (this.userId) {
						if (this.companyId) {
							filter.push({ param: 'companyId', value: this.companyId });
						}
						const result = await this.apiService.getUserEventHistory(
							this.userId,
							filter
						);
						this.finishedLoadingInitData = true;
						return result;
					}
					return [];
				})
			)
			.subscribe(
				(history) => {
					this.dataSource.data = history;
				},
				(error) => {
					console.error('Failed to fetch user event history', error);
					this.dataSource.data = [];
					this.finishedLoadingInitData = true;
				}
			);
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	async generateEventHistoryReport(): Promise<void> {
		this.isDownloadReportDisabled = true;
		if (this.userId) {
			await this.apiService
				.getEventHistoryReport(this.userId, 'user', this.filters)
				.then((_) => {
					this.isDownloadReportDisabled = false;
				});
		}
	}

	valueOrDashes(value: any) {
		if (value === undefined || value === null) {
			return '- - -';
		}
		return value;
	}

	rowId(row: EventHistory): string {
		if (
			!row ||
			!row.deviceId ||
			!row.utcStartDate ||
			!row.entityRelationshipId
		) {
			return 'unknown-id';
		}

		// normalize date to ISO with milliseconds :
		const normalizedDate = new Date(row.utcStartDate).toISOString();

		return `${row.deviceId}-${normalizedDate}-${row.entityRelationshipId}`;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['user'] || changes['companyId']) {
			this.updateEventHistoryAccess();
		}
	}

	private updateEventHistoryAccess(): void {
		this.canGenerateEventHistoryReport =
			(this.systemCapabilityService.has(
				SystemCapabilityEnum.userRoleManagement
			) &&
				this.systemCapabilityService.has(
					SystemCapabilityEnum.globalCompanyManagement
				)) ||
			(this.systemCapabilityService.has(SystemCapabilityEnum.runReports) &&
				this.companyId === undefined);
	}

	// Fetch company information to get company type and can access the 'care' features :
	async fetchCompanyType(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		let activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		if (activeCompanyId) {
			const companyData = await this.apiService.getCompanyById(activeCompanyId);
			this.companyTypeFlag = companyData?.company?.companyType === 'care';
		}

		this.displayedColumns = this.companyTypeFlag
			? [
					'serialNumber',
					'deviceOwner',
					'clientName',
					'clientIDN',
					'locationName',
					'deviceLocation',
					'eventType',
					'eventStart',
					'eventEnd',
					'endVisitType',
					'dwellTime',
					'rowMenu',
				]
			: [
					'serialNumber',
					'deviceOwner',
					'locationName',
					'deviceLocation',
					'eventType',
					'eventStart',
					'eventEnd',
					'endVisitType',
					'dwellTime',
					'rowMenu',
				];
	}
}
